'use client'

import type { Components } from 'react-markdown'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { cn } from '@/lib/utils'

/** Кастомные узлы Markdown — без зависимости от @tailwindcss/typography, стабильный вид в колокольчике и модалках. */

const markdownComponents: Partial<Components> = {
  p: ({ children, ...props }) => (
    <p className="mb-2.5 text-[13px] leading-relaxed text-foreground last:mb-0 [&:first-child]:mt-0" {...props}>
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through opacity-80">{children}</del>,
  h1: ({ children }) => (
    <h3 className="mb-2 mt-3 text-[1.05rem] font-bold tracking-tight text-foreground first:mt-0">{children}</h3>
  ),
  h2: ({ children }) => (
    <h3 className="mb-2 mt-3 text-[15px] font-semibold tracking-tight text-foreground first:mt-0">{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className="mb-1.5 mt-2.5 text-sm font-semibold text-foreground first:mt-0">{children}</h4>
  ),
  ul: ({ children, className }) => (
    <ul
      className={cn(
        'my-2 ml-4 list-disc space-y-1 pl-1 text-[13px] leading-relaxed marker:text-primary/90',
        className,
      )}
    >
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 ml-4 list-decimal space-y-1 pl-1 text-[13px] leading-relaxed marker:font-semibold marker:text-primary">
      {children}
    </ol>
  ),
  li: ({ children, className }) => (
    <li className={cn('leading-relaxed [--tw-prose-bullets:var(--foreground)]', className)}>
      {children}
    </li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-[3px] border-primary/50 bg-muted/35 py-2 pl-3 pr-2 text-muted-foreground rounded-r-lg">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary underline underline-offset-2 hover:opacity-90"
    >
      {children}
    </a>
  ),
  hr: () => (
    <hr className="my-4 border-0 border-t border-border/80" />
  ),
  code: ({ className, children }) => {
    const isBlock = Boolean(className && String(className).includes('language-'))
    if (isBlock) {
      return (
        <code className={cn('block font-mono text-[12px] text-foreground', className)}>
          {children}
        </code>
      )
    }
    return (
      <code className="rounded-md border border-border/80 bg-muted/60 px-1.5 py-0.5 font-mono text-[12px] text-foreground">
        {children}
      </code>
    )
  },
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-xl border border-border bg-muted/40 p-3 text-[12px] leading-snug [&>code]:bg-transparent [&>code]:p-0 [&>code]:border-0">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-border">
      <table className="w-full min-w-[240px] border-collapse text-[12px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-muted/55">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="border-b border-border px-2.5 py-2 text-left font-semibold text-foreground">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border-b border-border/70 px-2.5 py-1.5 text-muted-foreground">{children}</td>
  ),
  tr: ({ children }) => (
    <tr className="last:[&>td]:border-b-0">{children}</tr>
  ),
  img: ({ src, alt }) => (
    <span className="my-3 block overflow-hidden rounded-xl border border-border bg-muted/25">
      <img
        src={src ?? ''}
        alt={alt ?? ''}
        className="mx-auto max-h-[min(360px,50vh)] w-full max-w-full object-contain"
        loading="lazy"
      />
    </span>
  ),
}

type NotificationMarkdownProps = {
  /** Сырой Markdown из API (`body_markdown`). */
  content: string
  /** Дополнительные классы обёртки. */
  className?: string
}

/**
 * Единое отображение тела новости/уведомления для клиента и предпросмотра в админке.
 *
 * Поддержка GFM: таблицы, зачёркивание, чеклисты (как маркеры), ссылки открываются в новой вкладке.
 */
export function NotificationMarkdown({ content, className }: NotificationMarkdownProps) {
  const md = content?.trim() ? content : '—'

  return (
    <div
      data-notification-markdown
      className={cn(
        'notification-markdown-root text-[13px] text-foreground',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {md}
      </ReactMarkdown>
    </div>
  )
}
